import {
  useCallback,
  useRef,
  useState,
} from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';

const MIN_SCALE = 1;
const DEFAULT_MAX_SCALE = 4;
const DOUBLE_TAP_SCALE = 2;

interface Point {
  x: number;
  y: number;
}

const distance = (a: Point, b: Point) => Math.hypot(a.x - b.x, a.y - b.y);
const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

/**
 * Pinch-to-zoom + pan para uma imagem, via Pointer Events (sem dependência externa).
 *
 * O elemento que recebe os handlers precisa de `touch-action: none` no CSS, senão o
 * navegador captura o gesto antes. O zoom é ancorado no centro (`transform-origin`
 * padrão), e o pan fica limitado ao excedente da imagem para ela não sumir da tela.
 */
export default function usePinchZoom(maxScale: number = DEFAULT_MAX_SCALE) {
  const [scale, setScale] = useState(MIN_SCALE);
  const [offset, setOffset] = useState<Point>({ x: 0, y: 0 });

  const targetRef = useRef<HTMLImageElement>(null);
  const pointers = useRef(new Map<number, Point>());
  const pinchStart = useRef<{ distance: number; scale: number } | null>(null);
  const panStart = useRef<{ point: Point; offset: Point } | null>(null);

  // Com transform-origin no centro, a imagem só pode deslizar metade do que "sobra".
  const clampOffset = useCallback((next: Point, currentScale: number): Point => {
    const element = targetRef.current;
    if (!element) return next;
    const maxX = (element.offsetWidth * (currentScale - 1)) / 2;
    const maxY = (element.offsetHeight * (currentScale - 1)) / 2;
    return {
      x: clamp(next.x, -maxX, maxX),
      y: clamp(next.y, -maxY, maxY),
    };
  }, []);

  const reset = useCallback(() => {
    setScale(MIN_SCALE);
    setOffset({ x: 0, y: 0 });
    pinchStart.current = null;
    panStart.current = null;
    pointers.current.clear();
  }, []);

  const toggleZoom = useCallback(() => {
    setScale((current) => (current > MIN_SCALE ? MIN_SCALE : DOUBLE_TAP_SCALE));
    setOffset({ x: 0, y: 0 });
  }, []);

  const onPointerDown = useCallback((event: ReactPointerEvent) => {
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const points = Array.from(pointers.current.values());

    if (points.length === 2) {
      pinchStart.current = { distance: distance(points[0], points[1]), scale };
      panStart.current = null;
    } else if (points.length === 1) {
      panStart.current = { point: points[0], offset };
    }
  }, [scale, offset]);

  const onPointerMove = useCallback((event: ReactPointerEvent) => {
    if (!pointers.current.has(event.pointerId)) return;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const points = Array.from(pointers.current.values());

    if (points.length >= 2 && pinchStart.current) {
      const start = pinchStart.current;
      const next = clamp(
        (start.scale * distance(points[0], points[1])) / start.distance,
        MIN_SCALE,
        maxScale,
      );
      setScale(next);
      setOffset((current) => (next === MIN_SCALE ? { x: 0, y: 0 } : clampOffset(current, next)));
      return;
    }

    if (points.length === 1 && panStart.current && scale > MIN_SCALE) {
      const start = panStart.current;
      setOffset(clampOffset({
        x: start.offset.x + (points[0].x - start.point.x),
        y: start.offset.y + (points[0].y - start.point.y),
      }, scale));
    }
  }, [scale, maxScale, clampOffset]);

  const onPointerUp = useCallback((event: ReactPointerEvent) => {
    pointers.current.delete(event.pointerId);
    const points = Array.from(pointers.current.values());

    if (points.length < 2) pinchStart.current = null;
    // Se sobrou um dedo depois do pinch, ele reassume o pan sem precisar levantar e tocar de novo.
    panStart.current = points.length === 1 ? { point: points[0], offset } : null;
  }, [offset]);

  return {
    targetRef,
    scale,
    isZoomed: scale > MIN_SCALE,
    transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
    reset,
    toggleZoom,
    onPointerDown,
    onPointerMove,
    onPointerUp,
  };
}
